import type { Timed, WorkerRequest, WorkerResponse } from "./protocol";
import type { DecodeResult, DiffModel, DiffOptions } from "./types";

type Pending = {
  resolve: (value: unknown) => void;
  reject: (err: Error) => void;
};

/**
 * Promise-based RPC wrapper around a Worker that hosts the diff-core WASM
 * module. Concurrent calls are multiplexed via numeric request IDs.
 */
export class DiffWorker {
  private worker: Worker;
  private nextId = 1;
  private pending = new Map<number, Pending>();

  constructor(worker: Worker) {
    this.worker = worker;
    this.worker.addEventListener("message", (e: MessageEvent<WorkerResponse>) => {
      this.onMessage(e.data);
    });
    this.worker.addEventListener("error", (e: ErrorEvent) => {
      // Reject all pending — the worker has crashed.
      const err = new Error(`Worker crashed: ${e.message}`);
      for (const p of this.pending.values()) p.reject(err);
      this.pending.clear();
    });
  }

  private onMessage(msg: WorkerResponse): void {
    const pending = this.pending.get(msg.id);
    if (!pending) {
      // Stale response (caller already gone) — silently drop.
      return;
    }
    this.pending.delete(msg.id);
    if (msg.ok) {
      pending.resolve(msg.value);
    } else {
      pending.reject(new Error(msg.error));
    }
  }

  private send<T>(req: Omit<WorkerRequest, "id">, transfer: Transferable[] = []): Promise<T> {
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, {
        resolve: resolve as (v: unknown) => void,
        reject,
      });
      this.worker.postMessage({ ...req, id } as WorkerRequest, transfer);
    });
  }

  detectAndDecode(left: Uint8Array, right: Uint8Array): Promise<Timed<DecodeResult>> {
    // Don't transfer — caller may still want the Uint8Arrays.
    return this.send<Timed<DecodeResult>>({ cmd: "detectAndDecode", left, right } as Omit<WorkerRequest, "id">);
  }

  diff(leftText: string, rightText: string, opts: DiffOptions = {}): Promise<Timed<DiffModel>> {
    return this.send<Timed<DiffModel>>({ cmd: "diff", leftText, rightText, opts } as Omit<WorkerRequest, "id">);
  }

  exportPatch(model: DiffModel, leftText: string, rightText: string, context = 3): Promise<Timed<string>> {
    return this.send<Timed<string>>({ cmd: "exportPatch", model, leftText, rightText, context } as Omit<WorkerRequest, "id">);
  }

  terminate(): void {
    this.worker.terminate();
    for (const p of this.pending.values()) {
      p.reject(new Error("Worker terminated"));
    }
    this.pending.clear();
  }
}

/**
 * Spawn a fresh DiffWorker backed by the bundled worker module.
 * The `new URL(...)` form is what Vite recognizes for code-splitting Workers.
 */
export function createDiffWorker(): DiffWorker {
  const worker = new Worker(new URL("./worker.ts", import.meta.url), {
    type: "module",
  });
  return new DiffWorker(worker);
}
